import React, {Component} from 'react';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import {connector} from '../store';
import WatchablePreview from './WatchablePreview.jsx';
import {getPlayingVideoById} from '../helpers';
import flag from '../../Feature/Feature';

import ga from '../../../vendor/google-analytics';

function episodeSort(video1, video2) {

  // If both videos have a season+episode order them by the ordinal.
  // Otherwise, fallback to airdates
  if (video2.fullLength.episodeWithinSeason && video1.fullLength.episodeWithinSeason) {
    return video2.fullLength.episodeWithinSeason - video1.fullLength.episodeWithinSeason;
  }

  return video2.fullLength.airdate - video1.fullLength.airdate;
}

function languageFilter(language) {
  if (flag('video_languages')) {
    return function (video) {
      return !video.fullLength.language || video.fullLength.language === language;
    }
  } else {
    return () => true;
  }
}

let langMap = {
  en: 'English',
  es: 'Spanish'
};

export class EpisodeList extends Component {
  trackingCategory: string;

  constructor(props) {
    super(props);

    this.state = {
      languages: [],
      language: null
    }
  }

  componentDidMount() {
    if (this.props.show.seasons.length > 0) {
      let defaultSeason = this.getDefaultSeason();

      if (defaultSeason) {
        this.props.changeSeason(defaultSeason);
      } else {
        this.props.changeSeason(this.props.show.seasons[this.props.show.seasons.length - 1]);
      }
    }

    this.trackingCategory = 'Show | EpisodeList';
  }

  static getDerivedStateFromProps(props, state) {
    let languagePartitionedVideos = EpisodeList.partitionVideosByLanguage(props.videos.fullLengthVideos);

    let languages = Object.keys(languagePartitionedVideos);

    if (!languages.includes(state.language)) {
      let loadedVideo = getPlayingVideoById(props.player.container, props.player.currentlyPlaying.id);
      let language = loadedVideo && loadedVideo.language;

      if (!language) {
        language = languages[0];
      }

      return {
        ...state,
        languages,
        language
      };
    }

    return {
      ...state,
      languages
    };
  }

  getDefaultSeason() {
    let loadedVideo = getPlayingVideoById(this.props.player.container, this.props.player.currentlyPlaying.id);
    let defaultSeason = loadedVideo && this.props.show.seasons.filter(season => {
        return season.ordinal === loadedVideo.season;
      });

    return defaultSeason.length > 0 && defaultSeason[0] || null;
  }

  findSeason(id) {
    let seasons = this.props.show.seasons.filter(s => id === s.id);

    if (seasons.length > 0) {
      return seasons[0];
    }

    return null;
  }

  changeSeason(season) {
    this.props.changeSeason(season);
  }

  static partitionVideosByLanguage(videos) {
    let buckets = {};

    videos.forEach(video => {
      let language = video.fullLength.language;

      if (!language) {
        language = 'en';
      }

      if (buckets[language] === undefined) {
        buckets[language] = [];
      }

      buckets[language].push(video);
    });

    return buckets;
  }

  render() {
    if (this.props.videos.fullLengthVideos.length < 1 && this.props.show.seasons.length < 1) {
      return <div></div>;
    }

    let loadedVideo = getPlayingVideoById(this.props.player.container, this.props.player.currentlyPlaying.id);

    let defaultSeason = loadedVideo && this.props.show.seasons.filter(season => {
        return season.ordinal === loadedVideo.season;
      });

    if (defaultSeason.length === 0) {
      defaultSeason = this.props.show.seasons.slice(-1);
    }

    // Inspect the first season to determine how to order the display
    let ordinalSeason = this.props.show.seasons.length > 0 && this.props.show.seasons[0].ordinal > 999;

    // Create the season change handler
    let handler = function(e) {
      let season = this.findSeason(e.currentTarget.value);

      if (season) {
        let label = [this.props.show.title, this.props.videos.season.ordinal, season.ordinal].join(' | ');
        ga('send', 'event', this.trackingCategory, 'Change Season | [P]', label);

        this.changeSeason(season);
      }
    }.bind(this);

    let seasonSelect = classes => <select className={'show-seasons ' + classes} onChange={handler} defaultValue={defaultSeason.length > 0 && defaultSeason[0].id}>
      {this.props.show.seasons.map(s => {
        return <option key={s.id} value={s.id}>
          {s.ordinal === -1 ? 'Specials' : (ordinalSeason ? `${s.ordinal} Season` : `Season ${s.ordinal}`)}
        </option>;
      })}
    </select>;

    // Create the language change handler
    let languageHandler = function(e) {
      let language = e.currentTarget.value;
      this.setState({
        language
      })
    }.bind(this);

    let languageSelect = classes => <select className={'show-languages ' + classes} onChange={languageHandler} value={this.state.language}>
      {this.state.languages.map(language => {
        return <option key={language} value={language}>
          {langMap[language] || language}
        </option>;
      })}
    </select>;

    let filteredVideos = this.state.languages.length > 1 ?
      this.props.videos.fullLengthVideos.filter(languageFilter(this.state.language)) :
      this.props.videos.fullLengthVideos;

    return (
      <div className="episode-list row">
        <div className="small-12 columns">
          <div className="row">
            <div className={'small-12 episode-list-header section-header columns' + (this.props.show.seasons.length > 1 ? ' has-seasons' : '')}>
              <div>
              {this.props.show.seasons.length > 1 && seasonSelect('season-left')}
              {flag('video_languages') && this.state.languages.length > 1 && languageSelect('season-left')}
              </div>
              <h3 className="episode-list-title headline">Videos</h3>
              <div>
              {this.props.show.seasons.length > 1 && seasonSelect('season-right')}
              {flag('video_languages') && this.state.languages.length > 1 && languageSelect('season-right')}
              </div>
            </div>
            {this.props.videos.fullLengthVideos.length > 0 && <div className="small-12 columns">
              <TransitionGroup className="small-block-grid-1 large-block-grid-2 xlarge-block-grid-3">
                {filteredVideos.sort(episodeSort).map(v => {
                  return <CSSTransition
                    classNames="react-fade-in"
                    timeout={{enter: 500, exit: 500}}
                    key={v.fullLength.id}
                    in={true}>
                    <li>
                      <WatchablePreview video={v.fullLength} onClick={() => {
                        let label = [
                          this.props.show.title,
                          this.props.player.container.fullLength ? this.props.player.container.fullLength.title : 'None',
                          v.fullLength.title,
                          v.fullLength.tpMediaId
                        ].join(' | ');

                        ga('send', 'event', this.trackingCategory, 'Click Episode | [A]', label);
                      }} />
                    </li>
                  </CSSTransition>;
                })}
              </TransitionGroup>
            </div>}
          </div>
        </div>
      </div>
    );
  }
}

export default connector(EpisodeList);